import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, Lock, User as UserIcon, Building2, Search, ChevronDown, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ObserverLogin() {
  const navigate = useNavigate();
  const { observerLogin } = useAuth();
  const [societies, setSocieties] = useState([]);
  const [loadingSocieties, setLoadingSocieties] = useState(true);
  const [selected, setSelected] = useState(null);          // {id, organization_name, ...}
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/societies-with-observer`)
      .then(r => setSocieties(r.data.societies || []))
      .catch(() => toast.error('Failed to load societies'))
      .finally(() => setLoadingSocieties(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error('Select your society first');
    if (!username || !password) return toast.error('Enter username and password');
    setBusy(true);
    try {
      await observerLogin(selected.id, username.trim(), password);
      toast.success(`Signed in as Observer of ${selected.organization_name}`);
      navigate('/observer/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy to-brandpurple flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl p-8 shadow-2xl" data-testid="observer-login-page">
        <div className="flex items-center gap-2 mb-2">
          <Eye className="w-6 h-6 text-brandpurple" />
          <span className="text-xs font-bold uppercase text-brandpurple">Society Observer Login</span>
        </div>
        <h1 className="text-2xl font-black text-navy">View-only access</h1>
        <p className="text-sm text-slate-600 mb-6 mt-1">
          Select your society, then sign in with the credentials issued by your Buyer Admin.
        </p>
        <form onSubmit={submit} className="space-y-3" data-testid="observer-login-form">
          {/* 1. Society */}
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">
              1 · Select Society <span className="text-red-500">*</span>
            </label>
            <SocietyPicker
              societies={societies}
              selected={selected}
              onSelect={setSelected}
              loading={loadingSocieties}
            />
          </div>
          {/* 2. Username */}
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">2 · Username</label>
            <div className="relative">
              <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value.replace(/\s/g, ''))}
                required
                disabled={!selected}
                className="w-full pl-10 pr-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-brandpurple/40 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                placeholder="observer-username"
                data-testid="observer-username-input"
              />
            </div>
          </div>
          {/* 3. Password */}
          <div>
            <label className="block text-xs font-bold uppercase text-slate-500 mb-1">3 · Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={!selected}
                className="w-full pl-10 pr-3 py-2 rounded-md border border-slate-300 focus:ring-2 focus:ring-brandpurple/40 text-sm disabled:bg-slate-50 disabled:text-slate-400"
                data-testid="observer-password-input"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={busy || !selected}
            className="w-full px-4 py-3 rounded-md bg-navy text-white font-bold disabled:opacity-50"
            data-testid="observer-login-submit"
          >
            {busy ? 'Signing in…' : 'Login'}
          </button>
        </form>
        <div className="mt-5 pt-4 border-t border-slate-100 text-xs text-slate-500">
          Not an Observer?{' '}
          <Link to="/login" className="text-brandpurple font-bold hover:underline" data-testid="link-business-login">
            Go to Business Login →
          </Link>
        </div>
        <div className="mt-3 p-2.5 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
          <strong>Observer accounts have view-only access.</strong> You cannot approve, reject, comment on, or modify any procurement.
          If you've forgotten your password, contact your Buyer Admin.
        </div>
      </div>
    </div>
  );
}

function SocietyPicker({ societies, selected, onSelect, loading }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return societies;
    return societies.filter(x =>
      (x.organization_name || '').toLowerCase().includes(s) ||
      (x.city || '').toLowerCase().includes(s) ||
      (x.state || '').toLowerCase().includes(s)
    );
  }, [societies, q]);

  return (
    <div ref={ref} className="relative" data-testid="society-picker">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-slate-300 hover:border-brandpurple/50 focus:ring-2 focus:ring-brandpurple/40 text-sm text-left"
        data-testid="society-picker-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
          <span className={`truncate ${selected ? 'text-navy font-bold' : 'text-slate-400'}`}>
            {selected ? selected.organization_name : (loading ? 'Loading societies…' : 'Choose your society')}
          </span>
        </div>
        {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden">
          <div className="p-2 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                autoFocus
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search by name or city…"
                className="w-full pl-8 pr-3 py-1.5 rounded border border-slate-200 text-sm"
                data-testid="society-search-input"
              />
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-slate-500">
                {societies.length === 0 ? 'No societies with active Observers yet.' : 'No matches. Try a different search.'}
              </div>
            ) : (
              filtered.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => { onSelect(s); setOpen(false); setQ(''); }}
                  className={`w-full text-left px-3 py-2 hover:bg-brandpurple/5 text-sm ${selected?.id === s.id ? 'bg-brandpurple/5' : ''}`}
                  data-testid={`society-option-${s.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-navy flex-1">{s.organization_name}</span>
                    {s.is_demo && <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-black uppercase" data-testid="demo-chip">DEMO</span>}
                  </div>
                  {(s.city || s.state) && <div className="text-[11px] text-slate-500">{[s.city, s.state].filter(Boolean).join(' · ')}</div>}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
